/**
 * Service d'authentification
 *
 * Référence: docs/API_ENDPOINTS.md - Sections "Authentification Admin" et "Authentification Coach"
 *
 * Routes Admin:
 * - POST /api/admin/auth/login
 * - GET  /api/admin/auth/profile
 *
 * Routes Coach:
 * - POST /api/coach/auth/register
 * - POST /api/coach/auth/login
 * - GET  /api/coach/auth/profile
 * - PUT  /api/coach/auth/profile
 * - POST /api/coach/auth/forgot-password
 * - PUT  /api/coach/auth/reset-password/:token
 */

import api from './api';

export const authService = {
  // ==================== ADMIN ====================

  /**
   * Connexion admin
   * @param {string} email
   * @param {string} password
   * @returns {Promise<{_id, email, firstName, lastName, role, token}>}
   */
  loginAdmin: (email, password) =>
    api.post('/admin/auth/login', { email, password }, false),

  /**
   * Récupère le profil admin
   * @returns {Promise<Object>}
   */
  getAdminProfile: () =>
    api.get('/admin/auth/profile'),

  // ==================== COACH ====================

  /**
   * Inscription coach (crée automatiquement un tenant)
   * @param {Object} data - { email, password, firstName, lastName, phone? }
   * @returns {Promise<{_id, email, firstName, lastName, tenantId, tenantStatus, subscriptionPlan, maxClients, isSubscribed, token}>}
   */
  registerCoach: (data) =>
    api.post('/coach/auth/register', data, false),

  /**
   * Connexion coach
   * @param {string} email
   * @param {string} password
   * @returns {Promise<{_id, email, firstName, lastName, tenantId, tenantStatus, subscriptionPlan, maxClients, isSubscribed, token}>}
   */
  loginCoach: (email, password) =>
    api.post('/coach/auth/login', { email, password }, false),

  /**
   * Connexion unifiee (coach ou client)
   * Detecte automatiquement le type d'utilisateur
   * @param {string} email
   * @param {string} password
   * @returns {Promise<{userType, _id, email, firstName, lastName, tenantId, token, isSubscribed?}>}
   */
  unifiedLogin: (email, password) =>
    api.post('/coach/auth/unified-login', { email, password }, false),

  /**
   * Récupère le profil coach
   * @returns {Promise<Object>}
   */
  getCoachProfile: () =>
    api.get('/coach/auth/profile'),

  /**
   * Met à jour le profil coach
   * @param {Object} data - Données à mettre à jour
   * @returns {Promise<Object>}
   */
  updateCoachProfile: (data) =>
    api.put('/coach/auth/profile', data),

  /**
   * Demande de réinitialisation de mot de passe
   * @param {string} email
   * @returns {Promise<{message}>}
   */
  forgotPassword: (email) =>
    api.post('/coach/auth/forgot-password', { email }, false),

  /**
   * Réinitialise le mot de passe avec le token
   * @param {string} token - Token de réinitialisation
   * @param {string} password - Nouveau mot de passe
   * @returns {Promise<Object>}
   */
  resetPassword: (token, password) =>
    api.put(`/coach/auth/reset-password/${token}`, { password }, false),

  // ==================== UTILITAIRES ====================

  /**
   * Détermine le type d'utilisateur à partir du token décodé
   * @param {Object} decoded - Token JWT décodé
   * @returns {'admin' | 'coach' | 'client'}
   */
  getUserType: (decoded) => {
    if (decoded.role === 'admin' || decoded.role === 'super_admin') return 'admin';
    if (decoded.role === 'client' || decoded.type === 'client') return 'client';
    if (decoded.tenantId) return 'coach';
    return 'coach'; // Par défaut
  },

  /**
   * Vérifie si un coach est abonné
   * @param {Object} user - Données utilisateur
   * @returns {boolean}
   */
  isCoachSubscribed: (user) => {
    return user?.isSubscribed === true;
  },

  /**
   * Vérifie si le tenant est actif
   * @param {Object} user - Données utilisateur
   * @returns {boolean}
   */
  isTenantActive: (user) => {
    return user?.tenantStatus === 'active';
  },
};

export default authService;
